import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/ui/FormField';
import { api, type IBankAccount } from '../../api/client';
import type { SchemaFieldProps } from '../types';

/**
 * Custom SchemaForm widget for an activity's `bankAccountId`.
 *
 * The generic fk widget can't express this field's central case: null is a
 * meaningful value ("collect into whatever the organization default is"), not
 * an empty selection. It also has no way to label an option with both the
 * account name and its number, which is what actually lets an admin tell two
 * accounts apart.
 *
 * value is number | null.
 */
const BankAccountField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<IBankAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.organization.bankAccounts.list();
        if (!cancelled) setAccounts(res.data);
      } catch {
        // Non-fatal: the select falls back to "organization default" only,
        // which is what an activity with no override does anyway.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const defaultAccount = useMemo(() => accounts.find((a) => a.isDefault), [accounts]);

  const label = (account: IBankAccount) =>
    account.name ? `${account.name} — ${account.accountNumber}` : account.accountNumber;

  // Name the account actually inherited, so "default" isn't an abstraction the
  // admin has to go and look up in another page.
  const inheritLabel = defaultAccount
    ? t('activities.bankAccountInherit', { account: label(defaultAccount) })
    : t('activities.bankAccountInheritNone');

  return (
    <FormField
      htmlFor="sf-bankAccountId"
      label={t(field.labelKey, field.name)}
      error={error}
      helperText={t('activities.bankAccountHelp')}
    >
      <select
        id="sf-bankAccountId"
        value={value === null || value === undefined ? '' : String(value)}
        disabled={loading}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">{loading ? t('common.loading') : inheritLabel}</option>
        {accounts.map((account) => (
          <option key={account._id} value={account._id}>
            {label(account)}
          </option>
        ))}
      </select>
    </FormField>
  );
};

export default BankAccountField;
